import { Models } from "api/types";

type PromotionModel = Models<"PromotionModel">;

export const hasAvailableTreatmentsToSelect = (
  membershipsResponse: Models<"PlatformUserMembershipModel">[],
): boolean => {
  return membershipsResponse.some((membershipObj) => {
    return membershipObj.membership.membershipRewardGroups?.some(
      (rg) => rg.hasSelectableRewards && rg.availableTreatmentsAmount > 0 && rg.periodMonths !== 0,
    );
  });
};

/**
 * Check if there are any promotions that have been used in purchases.
 */
export const hasOffersUsedInPurchases = (promotions?: PromotionModel[]): boolean => {
  return promotions?.some((p) => p.isDiscountUsed) ?? false;
};

/**
 * Filter promotions to only those that have been used in purchases.
 */
export const getOffersUsedInPurchases = (promotions?: PromotionModel[]): PromotionModel[] => {
  return promotions?.filter((p) => p.isDiscountUsed) ?? [];
};

/**
 * Get available offers that should be shown in the "Available Offers" section.
 * Shows offers that have NOT been used in a purchase and are either:
 * - Active and not yet redeemed (claimed), OR
 * - In cart pending checkout (membership promos), OR
 * - Redeemed (claimed) regular discount promos that haven't been used yet
 *
 * NOTE: Free service promos (with freePackageGroup) are excluded once claimed,
 * because the free package is granted immediately upon claiming.
 * NOTE: In-store redeemable offers are excluded once claimed,
 * because they are fulfilled immediately by staff in-store.
 */
export const getAvailableOffers = (promotions?: PromotionModel[]): PromotionModel[] => {
  if (!promotions) return [];

  return promotions.filter(
    (p) =>
      !p.isDiscountUsed &&
      ((p.active && !p.redeemedOn) ||
        p.hasPendingCartItems ||
        (p.redeemedOn && !p.freePackageGroup && !p.isInStoreRedeemable)),
  );
};

/**
 * Get redeemed offers that should be shown in the "Redeemed Offers" section.
 * Shows offers that were redeemed within the last 2 weeks, including:
 * - Free service promos (redeemedOn set, freePackageGroup present - package granted on claim)
 * - Regular discount promos used in purchase (isDiscountUsed true)
 * - Promos used directly at checkout without claiming (redeemedOn null, isDiscountUsed true)
 *
 * Excludes:
 * - Promos still pending checkout (hasPendingCartItems = true)
 * - Claimed regular discount promos that haven't been used yet (stay in Available)
 */
export const getRedeemedOffers = (promotions?: PromotionModel[]): PromotionModel[] => {
  if (!promotions) return [];

  const now = new Date();
  // 14 days in milliseconds (14 * 24 * 60 * 60 * 1000)
  const twoWeeksAgo = new Date(now.getTime() - 1209600000);

  return promotions.filter((p) => {
    // Exclude promos that are still pending checkout (in cart but not purchased)
    if (p.hasPendingCartItems) {
      return false;
    }

    // If redeemedOn exists, check if it qualifies for Redeemed section
    if (p.redeemedOn) {
      const redeemedDate = new Date(p.redeemedOn);
      const isWithinTwoWeeks = redeemedDate >= twoWeeksAgo;

      // Free service promos: show in Redeemed once claimed (package granted immediately)
      // In-store redeemable promos: show in Redeemed once claimed (fulfilled by staff in-store)
      // Regular discount promos: only show in Redeemed if actually used in purchase
      return (
        isWithinTwoWeeks && (!!p.freePackageGroup || p.isDiscountUsed || p.isInStoreRedeemable)
      );
    }

    // No redeemedOn means used directly at checkout without claiming - show if used
    return p.isDiscountUsed;
  });
};

/**
 * Check if there are any offers to show (available or redeemed within 2 weeks).
 */
export const hasAnyOffersToShow = (promotions?: PromotionModel[]): boolean => {
  const availableOffers = getAvailableOffers(promotions);
  const redeemedOffers = getRedeemedOffers(promotions);

  return availableOffers.length > 0 || redeemedOffers.length > 0;
};
